/**
 * Pure decisions about session state, kept out of the provider so they can be
 * tested directly (same pattern as `preferred-org.utils`).
 */

/**
 * May `refreshAuth` commit a new session from these two answers?
 *
 * "Is there a session?" and "who is it?" are two independent network calls —
 * `ensureIdentity()` and `/iam/v4/iam/me`. They can disagree, and when only the
 * second fails, committing produced a session that claimed to be signed in with
 * no identity: empty `roles` (so every permission gate denied and screens showed
 * "Access restricted"), a null `appRole` ("Unknown role"), and an IAM user query
 * disabled for want of a user id — which reports `isLoading: false`, so the
 * provider's spinner never held.
 *
 * Nothing retried, so the broken state stuck until a reload.
 */
export const canCommitSession = (
  authenticated: boolean,
  decodedIdentity: unknown | null,
): boolean => authenticated && !!decodedIdentity;

/**
 * Is `roles` trustworthy yet?
 *
 * Permission gates must not deny while this is false: an unresolved session has
 * no roles, and "no roles yet" is not "denied".
 *
 * Two cases here were previously answered "resolved" when the honest answer is
 * "we don't know", and each of them rendered "Access restricted" over a screen
 * the user was entitled to — the state a tab reload appeared to "fix":
 *
 * 1. **Signed out while still holding an identity.** `authenticated: false`
 *    used to short-circuit to `true` ("signed out, no roles to wait for"). That
 *    is right on the login screen, but the flag also goes false when a session
 *    is torn down under a mounted app screen — and nothing routes on that
 *    transition, so the user sat on `/feeds` with `roles: []` and every gate
 *    denying. A lingering identity is the tell: genuinely signed out means no
 *    identity either.
 *
 * 2. **The user record resolved to nothing.** `fetchBlocksUser` answers `null`
 *    — not an error — for a 401/403, so the query reports `isLoading: false`
 *    and `isError: false` with no data. `hasIdentity && !isUserLoading` was
 *    therefore true while `roles` (a union of the identity's claim and the
 *    record's memberships) was empty for want of an answer, not for want of
 *    permission.
 */
export const isSessionResolved = ({
  initialized,
  authenticated,
  hasIdentity,
  hasUser,
  isUserLoading,
}: {
  initialized: boolean;
  authenticated: boolean;
  hasIdentity: boolean;
  hasUser: boolean;
  isUserLoading: boolean;
}): boolean => {
  if (!initialized) return false;
  if (!authenticated) return !hasIdentity;
  return hasIdentity && hasUser && !isUserLoading;
};

/**
 * Must the app route back to login?
 *
 * True only for the true→false transition — a session that ended while the app
 * was mounted. Nothing else routes on it (`app/index.tsx` guards only `/`, and
 * the tab layout has no guard), so without this the user stayed on a gated
 * screen with the identity and query cache torn out from under it: "No name",
 * "Unknown role", "Access restricted", until a manual reload.
 *
 * The account switcher is excluded: it drops the session on purpose and walks
 * the user through hosted login itself.
 */
export const shouldRouteToLogin = ({
  authenticated,
  wasAuthenticated,
  isSwitching,
}: {
  authenticated: boolean;
  wasAuthenticated: boolean;
  isSwitching: boolean;
}): boolean => !authenticated && wasAuthenticated && !isSwitching;

/**
 * Should the session be re-verified because the user record came back empty?
 *
 * `fetchBlocksUser` answers `null` — not an error — when the IAM read is
 * refused, so the query reports settled, not-loading and not-errored with no
 * data. Neither the provider's spinner nor its retry screen catches that, and
 * the 401 interceptor cannot either: `/iam/me` and `/iam/users/{id}` go out on a
 * raw `fetch`, not through `apiClient`. The app just kept rendering with no
 * roles.
 *
 * `alreadyTried` keeps this to one attempt per identity, so a session that is
 * genuinely refused settles instead of retrying forever.
 */
export const shouldRecheckSession = ({
  authenticated,
  hasIdentity,
  hasUser,
  isUserLoading,
  isUserError,
  alreadyTried,
}: {
  authenticated: boolean;
  hasIdentity: boolean;
  hasUser: boolean;
  isUserLoading: boolean;
  isUserError: boolean;
  alreadyTried: boolean;
}): boolean =>
  authenticated &&
  hasIdentity &&
  !hasUser &&
  !isUserLoading &&
  !isUserError &&
  !alreadyTried;
